import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Admin } from "../../types/Admin.ts";
import { getAdmins } from "../../services/admins.service.ts";
import { PageContainer, PageTitle } from "../../components/Layout.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";

const AdminsListContainer = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  overflow: hidden;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const AdminItem = styled.li`
  cursor: pointer;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  color: #001f52;
  
  .dark-mode & {
    border-bottom-color: #3a3a4e;
    color: #e0e0e0;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #e8f5e9;
    padding-left: 2rem;
    color: #001f52;
  }

  .dark-mode &:hover {
    background-color: #3a3a4e;
    color: #64da4c;
  }
`;

const AdminName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #001f52;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const AdminEmail = styled.div`
  font-size: 0.9rem;
  color: #666;
  padding-top: 0.5rem;
  border-top: 1px solid #e8e8e8;
  transition: color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
    border-top-color: #3a3a4e;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border-left: 4px solid #c62828;
  transition: background-color 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background-color: #3a1f1f;
    color: #ff6b6b;
    border-left-color: #ff6b6b;
  }
`;

const LoadingMessage = styled.div`
  color: #666;
  padding: 1rem;
  text-align: center;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

export default function Admins() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadAdmins = () => {
    setLoading(true);
    setError(null);
    getAdmins()
      .then((admins) => {
        setAdmins(admins);
        setError(null);
      })
      .catch((err) => {
        const userFriendlyError = parseApiError(err);
        setError(userFriendlyError);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadAdmins();
  }, []);


  return (
    <PageContainer>
      <PageTitle>Admins</PageTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {loading && <LoadingMessage>Indlæser admins...</LoadingMessage>}
      
      {!loading && !error && admins.length > 0 && (
        <>
          <AdminsListContainer>
            {admins.map((admin) => (
              <AdminItem key={admin.id} onClick={() => navigate(`/admins/${admin.id}`)}>
                <AdminName>{admin.name || "Ingen navn"}</AdminName>
                <AdminEmail>{admin.email}</AdminEmail>
              </AdminItem>
            ))}
          </AdminsListContainer>
        </>
      )}
      
      {!loading && !error && admins.length === 0 && (
        <LoadingMessage>Ingen admins fundet</LoadingMessage>
      )}
      
    </PageContainer>
  );
}
